import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Zap, CheckCircle2, XCircle, Plug, Loader2, Save, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ApiConnectionsTransfer } from "@/components/ApiConnectionsTransfer";

const AUTH_TYPE_LABELS = {
  none:            "None (public)",
  bearer:          "Bearer token",
  api_key_header:  "API key header",
  basic:           "Basic auth (username + password)",
  basic_token:     "Basic — pre-encoded API key",
  oauth2_client_credentials: "OAuth 2.0 — client credentials",
};

// Iter 274 — Stax Bill hands you a key that is already the base64 Basic blob,
// so it travels verbatim as `Authorization: Basic <key>` with no password.
// Iter 285 — Bandwidth retired API Users for OAuth 2.0 client credentials:
// the platform swaps Client ID + Secret for a Bearer at the token URL and
// caches it until it expires.
const AUTH_TYPE_HINTS = {
  basic:       "Sends base64(username:password) — the classic two-field Basic auth.",
  basic_token: "Sends Authorization: Basic <key> exactly as pasted — no username, no password. Use this for Stax Bill.",
  oauth2_client_credentials: "POSTs grant_type=client_credentials to the token URL with Client ID:Client Secret as Basic auth, then sends the returned access token as a Bearer — refreshed automatically before it expires. Use this for Bandwidth (Bandwidth App → Account → API Credentials).",
};

const EMPTY = {
  name: "", alias: "", base_url: "", auth_type: "none", secret: "",
  auth_username_or_header: "", token_url: "", oauth_scope: "", extra_headers: [], notes: "",
};

// Iter 110 — API connection registry. Secrets are Fernet-encrypted at
// rest; the UI shows a masked indicator only. Leave the secret blank
// on edit to keep the stored value.
// Iter 216 — `alias` ties a connection to the feature that resolves it
// (incident.io rota, UC Platform pulls…). Known aliases come from
// /api-connections/meta and surface as "Set up" suggestions until configured.
export function ApiConnectionsAdmin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const [{ data }, meta] = await Promise.all([api.get("/api-connections"), api.get("/api-connections/meta")]);
      setRows(data);
      setAliases(meta.data?.aliases || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load API connections");
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openSuggested = (a) => {
    setForm({ ...EMPTY, name: a.label, alias: a.alias, base_url: a.base_url || "", auth_type: a.auth_type || "bearer",
              token_url: a.token_url || "" });
    setEditing("new");
  };
  const openEdit = (r) => {
    setForm({
      name: r.name || "",
      alias: r.alias || "",
      base_url: r.base_url || "",
      auth_type: r.auth_type || "none",
      secret: "",
      auth_username_or_header: r.auth_username_or_header || "",
      token_url: r.token_url || "",
      oauth_scope: r.oauth_scope || "",
      extra_headers: r.extra_headers || [],
      notes: r.notes || "",
      __has_secret: r.has_secret,
    });
    setEditing(r.id);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const isOauth = form.auth_type === "oauth2_client_credentials";
    if (isOauth && !(form.token_url || "").trim()) { toast.error("Token URL is required for OAuth 2.0"); return; }
    if (isOauth && !(form.auth_username_or_header || "").trim()) { toast.error("Client ID is required for OAuth 2.0"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        alias: (form.alias || "").trim(),
        base_url: form.base_url || "",
        auth_type: form.auth_type,
        secret: form.secret === "" ? (editing === "new" ? "" : undefined) : form.secret,
        auth_username_or_header: form.auth_username_or_header || "",
        token_url: isOauth ? (form.token_url || "").trim() : "",
        oauth_scope: isOauth ? (form.oauth_scope || "").trim() : "",
        extra_headers: (form.extra_headers || []).filter((h) => h.name),
        notes: form.notes || "",
      };
      Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
      if (editing === "new") {
        await api.post("/api-connections", body);
        toast.success("API connection created");
      } else {
        await api.put(`/api-connections/${editing}`, body);
        toast.success("API connection updated");
      }
      close();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/api-connections/${id}/test`);
      if (data.ok) toast.success(`Connected · HTTP ${data.status} · credentials accepted`);
      else toast.error(`Test failed: ${data.error || "unknown error"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Test failed");
    } finally {
      setTesting(null);
    }
  };

  const del = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/api-connections/${deleting.id}`);
      toast.success("Deleted");
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Delete failed");
    }
  };

  const addHeader = () => setForm((f) => ({ ...f, extra_headers: [...(f.extra_headers || []), { name: "", value: "" }] }));
  const rmHeader = (i) => setForm((f) => ({ ...f, extra_headers: f.extra_headers.filter((_, idx) => idx !== i) }));
  const setHeader = (i, k, v) => setForm((f) => ({
    ...f, extra_headers: f.extra_headers.map((h, idx) => idx === i ? { ...h, [k]: v } : h),
  }));

  const secretLabel = form.auth_type === "basic" ? "Password"
                    : form.auth_type === "basic_token" ? "API key (sent verbatim after “Basic ”)"
                    : form.auth_type === "api_key_header" ? "API key value"
                    : form.auth_type === "bearer" ? "Bearer token"
                    : form.auth_type === "oauth2_client_credentials" ? "Client secret"
                    : "";
  const usernameLabel = form.auth_type === "basic" ? "Username"
                      : form.auth_type === "api_key_header" ? "Header name (e.g. X-API-Key)"
                      : form.auth_type === "oauth2_client_credentials" ? "Client ID"
                      : "";
  const isOauth = form.auth_type === "oauth2_client_credentials";
  const knownAlias = aliases.find((a) => a.alias === (form.alias || "").trim().toLowerCase().replace(/-/g, "_"));
  const missing = aliases.filter((a) => !a.configured);

  return (
    <Card className="border-slate-200 shadow-sm p-6 space-y-4" data-testid="api-connections-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
            <Plug className="h-4 w-4 text-slate-500" /> API Connections
          </h3>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            The single home for third-party API credentials. Features resolve a connection by its <span className="font-mono">alias</span> at call time —
            nothing is read from environment variables. Secrets are encrypted at rest; leave the secret blank on edit to keep the stored value.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {isSuper && <ApiConnectionsTransfer rows={rows || []} onImported={load} />}
          <Button onClick={openNew} data-testid="api-conn-new" className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" /> New
          </Button>
        </div>
      </div>

      <p className="px-1 pb-2 text-[11px] text-slate-400" data-testid="api-conn-health-note">
        Every connection is re-checked nightly. If one stops authenticating — on that check, a manual
        Test, or during a live sync — the health-alert recipients (or all super admins) get an email,
        and another when it recovers.
      </p>

      {rows !== null && missing.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-2" data-testid="api-conn-suggested">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">Expected by the platform · not configured yet</div>
          <div className="flex flex-wrap gap-2">
            {missing.map((a) => (
              <button key={a.alias} type="button" onClick={() => openSuggested(a)} data-testid={`api-conn-suggest-${a.alias}`}
                className="group flex items-center gap-2 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-left text-xs shadow-sm transition hover:border-amber-500">
                <span className="font-medium text-slate-800">{a.label}</span>
                <span className="font-mono text-[10px] text-slate-500">{a.alias}</span>
                <span className="text-amber-700 group-hover:underline">Set up →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {rows === null ? (
        <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500" data-testid="api-conn-empty">
          No API connections configured yet.
        </div>
      ) : (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 overflow-hidden">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`api-conn-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800 truncate">{r.name}</span>
                  {r.alias && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] text-primary" data-testid={`api-conn-alias-${r.id}`}>{r.alias}</span>
                  )}
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                    {AUTH_TYPE_LABELS[r.auth_type] || r.auth_type}
                  </span>
                  {r.has_secret && <span className="text-[10px] text-emerald-600">🔒 {r.secret_masked}</span>}
                  {/* Iter 277 — a connection that stopped authenticating is loud
                      here and emails the alert recipients once every 12h. */}
                  {r.last_test_ok === false && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700"
                      data-testid={`api-conn-failing-${r.id}`}>failing</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-slate-500 truncate">{r.base_url || "—"}</div>
                {r.used_by && <div className="mt-0.5 text-[11px] text-slate-400">Used by: {r.used_by}</div>}
                {r.last_tested_at && (
                  <div className={`mt-0.5 text-[11px] flex items-center gap-1 ${r.last_test_ok ? "text-emerald-600" : "text-red-600"}`}>
                    {r.last_test_ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    Last checked {new Date(r.last_tested_at).toLocaleString()}{!r.last_test_ok && r.last_test_error ? ` — ${r.last_test_error}` : ""}
                  </div>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => test(r.id)} disabled={testing === r.id} data-testid={`api-conn-test-${r.id}`} className="gap-1">
                {testing === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />} Test
              </Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`api-conn-edit-${r.id}`} className="gap-1">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDeleting({ id: r.id, name: r.name })} data-testid={`api-conn-delete-${r.id}`} className="gap-1 text-red-600 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="flex max-h-[calc(100vh-3rem)] max-w-2xl flex-col gap-0 p-0" data-testid="api-conn-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>{editing === "new" ? "New API Connection" : "Edit API Connection"}</DialogTitle>
            <DialogDescription>Encrypted at rest. Super-admin only.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 overflow-y-auto px-6 py-4 sm:grid-cols-2" data-testid="api-conn-dialog-body">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="api-form-name" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Alias <span className="ml-1 text-[10px] font-normal text-slate-400">(ties this connection to the feature that uses it)</span></Label>
              <Input value={form.alias} list="api-conn-alias-options" placeholder="e.g. incident_io"
                onChange={(e) => setForm((f) => ({ ...f, alias: e.target.value }))} className="font-mono" data-testid="api-form-alias" />
              <datalist id="api-conn-alias-options">
                {aliases.map((a) => <option key={a.alias} value={a.alias}>{a.label}</option>)}
              </datalist>
              {knownAlias
                ? <p className="text-xs text-emerald-700" data-testid="api-form-alias-usedby">Used by: {knownAlias.used_by}</p>
                : <p className="text-xs text-slate-400">Lowercase letters, digits and underscores. Pick a known alias to wire a platform feature, or invent one for your own integrations.</p>}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Base URL</Label>
              <Input value={form.base_url} onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))} placeholder="https://api.example.com" data-testid="api-form-baseurl" />
            </div>
            <div className="space-y-1.5">
              <Label>Auth type</Label>
              <Select value={form.auth_type} onValueChange={(v) => setForm((f) => ({ ...f, auth_type: v }))}>
                <SelectTrigger data-testid="api-form-authtype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(AUTH_TYPE_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {AUTH_TYPE_HINTS[form.auth_type] && (
                <p className="text-xs text-slate-400" data-testid="api-form-authtype-hint">
                  {AUTH_TYPE_HINTS[form.auth_type]}
                </p>
              )}
            </div>
            {(form.auth_type === "basic" || form.auth_type === "api_key_header" || isOauth) && (
              <div className="space-y-1.5">
                <Label>{usernameLabel}</Label>
                <Input value={form.auth_username_or_header} onChange={(e) => setForm((f) => ({ ...f, auth_username_or_header: e.target.value }))}
                  className={isOauth ? "font-mono" : ""} data-testid="api-form-username" />
              </div>
            )}
            {form.auth_type !== "none" && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{secretLabel} {form.__has_secret ? <span className="text-[10px] text-emerald-600 ml-1">(saved — leave blank to keep)</span> : null}</Label>
                <Input type="password" value={form.secret} onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))} data-testid="api-form-secret" />
              </div>
            )}
            {isOauth && (
              <>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Token URL <span className="ml-1 text-[10px] font-normal text-slate-400">(POST grant_type=client_credentials)</span></Label>
                  <Input value={form.token_url} placeholder="https://id.bandwidth.com/api/v1/oauth2/token"
                    onChange={(e) => setForm((f) => ({ ...f, token_url: e.target.value }))} className="font-mono" data-testid="api-form-token-url" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Scope <span className="ml-1 text-[10px] font-normal text-slate-400">(optional — Bandwidth needs none)</span></Label>
                  <Input value={form.oauth_scope} onChange={(e) => setForm((f) => ({ ...f, oauth_scope: e.target.value }))} data-testid="api-form-oauth-scope" />
                </div>
              </>
            )}
            <div className="sm:col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label>Extra headers (optional)</Label>
                <Button size="sm" variant="outline" type="button" onClick={addHeader} data-testid="api-form-add-header" className="gap-1 h-7">
                  <Plus className="h-3 w-3" /> Add header
                </Button>
              </div>
              {(form.extra_headers || []).map((h, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="Header name" value={h.name} onChange={(e) => setHeader(i, "name", e.target.value)} data-testid={`api-form-header-name-${i}`} />
                  <Input placeholder="Value" value={h.value} onChange={(e) => setHeader(i, "value", e.target.value)} data-testid={`api-form-header-value-${i}`} />
                  <Button size="sm" variant="outline" onClick={() => rmHeader(i)} data-testid={`api-form-header-rm-${i}`} className="shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="api-form-notes" />
            </div>
          </div>
          <DialogFooter className="border-t border-slate-100 px-6 py-4">
            <Button variant="outline" onClick={close} data-testid="api-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="api-form-save" className="gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete API Connection</DialogTitle>
            <DialogDescription>Delete <span className="font-medium text-slate-800">{deleting?.name}</span>? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button onClick={del} data-testid="api-conn-delete-confirm" className="bg-red-600 text-white hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
